import { createContext } from 'react';
import { parseActionLink, isValidEmail, isValidHttpUrl } from './helper';
import { isValidPhone } from './phoneValidation';

export const FREE_TEXT_DEFAULT_LABEL = 'None of these, mine is:';
export const FREE_TEXT_MAX = 300;

export const resolveEffectiveActionType = (actionType, linkValue) => {
    if (actionType !== 'link') return actionType || 'page';
    const parsed = parseActionLink(linkValue);
    return (parsed.url || parsed.mail || parsed.phone) ? 'link' : 'page';
};

export const navigatorLinkError = (linkValue) => {
    const { url, mail, phone } = parseActionLink(linkValue);
    if (mail && !isValidEmail(mail)) return 'Please enter a valid email address';
    if (phone && !isValidPhone(phone)) return 'Please enter a valid phone number, including its country code';
    if (url && !isValidHttpUrl(url)) return 'Please enter a valid URL';
    return null;
};

export const navigatorConfigLinkError = (c) => {
    if (!c) return null;
    if (c.mode === 'continue') return c.continueActionType === 'link' ? navigatorLinkError(c.continueLinkValue) : null;
    return (c.options || [])
        .map((o) => (o.actionType === 'link' ? navigatorLinkError(o.linkValue) : null))
        .find(Boolean) || null;
};

// Hover text for a Navigator option/CTA, mirroring NavigatorPromptElement#performAction (unset page = next page, or close on the last page)
export const describeNavigatorAction = ({ actionType, pageLink, url, mail, phone, currentPage, totalPages }) => {
    const type = actionType || 'page';
    const isUnset = pageLink === null || pageLink === undefined || pageLink === '';
    const isLastPage = !!totalPages && Number(currentPage) === Number(totalPages);
    const target = isUnset ? Number(currentPage) + 1 : Number(pageLink);
    const closes = type === 'close' || (isUnset && isLastPage);
    const pageText = closes ? 'Close Navigator' : (Number.isFinite(target) && target >= 1 ? `Jump to page ${target}` : null);

    if (type === 'optin') return closes || !pageText ? 'Show opt-in form' : `Show opt-in form, then jump to page ${target}`;
    if (type !== 'link') return pageText;
    const linkText = mail ? `Email ${mail}` : phone ? `Call ${phone}` : url ? `Open ${url}` : null;
    if (!linkText) return pageText;
    return closes || !pageText ? linkText : `${linkText}, then jump to page ${target}`;
};

// Same as describeNavigatorAction, for the editor's unsaved config shape (action / linkValue)
export const describeNavigatorConfigAction = ({ actionType, action, linkValue, currentPage, totalPages }) => {
    const effectiveType = resolveEffectiveActionType(actionType, linkValue);
    const link = effectiveType === 'link' ? parseActionLink(linkValue) : {};
    return describeNavigatorAction({ actionType: effectiveType, pageLink: action, ...link, currentPage, totalPages });
};

export const NAVIGATOR_TEXT_MAX = 255;

export const DEFAULT_NAVIGATOR_PAGE_CONFIG = {
    mode: 'continue',
    enabled: true,
    questionText: '',
    ctaText: '',
    continueAction: '',
    continueActionType: 'page',
    continueLinkValue: '',
    weightLevel: 'standard',
    guideInput: '',
    options: [],
    position: 'auto',
    delaySeconds: 5,
    status: null,
    hasUnapprovedChanges: false,
    latestCheckpointAt: null,
    questionTitleId: null,
    elementId: null,
    savedSnapshot: null,
    bgColor: '',
    textColor: '',
    borderColor: '',
    questionFontFamily: '',
    questionFontSize: '',
    questionFontWeight: '',
    optionFontFamily: '',
    optionFontSize: '',
    optionFontWeight: '',
};

export const buildNavigatorSnapshot = (cfg) => {
    const continueEffectiveType = resolveEffectiveActionType(cfg.continueActionType, cfg.continueLinkValue);
    return JSON.stringify({
    mode: cfg.mode,
    questionText: cfg.questionText,
    ctaText: cfg.ctaText,
    continueAction: cfg.continueAction,
    continueActionType: continueEffectiveType,
    continueLinkValue: continueEffectiveType === 'link' ? cfg.continueLinkValue : '',
    position: cfg.position,
    delaySeconds: cfg.delaySeconds,
    weightLevel: cfg.weightLevel,
    guideInput: cfg.guideInput || '',
    bgColor: cfg.bgColor,
    textColor: cfg.textColor,
    borderColor: cfg.borderColor,
    questionFontFamily: cfg.questionFontFamily,
    questionFontSize: cfg.questionFontSize,
    questionFontWeight: cfg.questionFontWeight,
    optionFontFamily: cfg.optionFontFamily,
    optionFontSize: cfg.optionFontSize,
    optionFontWeight: cfg.optionFontWeight,
    // weight is derived server-side from valence x strength, so it's excluded
    // here -- including it would mark the panel dirty after every save.
    options: cfg.options.map(o => {
        const effectiveType = resolveEffectiveActionType(o.actionType, o.linkValue);
        return { label: o.label, tag: o.tag, isHardDq: o.isHardDq, advancedWeight: o.advancedWeight, freeText: !!o.freeText, valence: o.valence, strength: o.strength, action: o.action, actionType: effectiveType, linkValue: effectiveType === 'link' ? o.linkValue : '', bgColor: o.bgColor, borderColor: o.borderColor, hoverColor: o.hoverColor, textColor: o.textColor };
    }),
    });
};

export const buildNavigatorPayload = (c, pageNumber) => {
    const continueEffectiveType = resolveEffectiveActionType(c.continueActionType, c.continueLinkValue);
    const continueLink = parseActionLink(c.continueLinkValue);
    return {
    question_type: c.mode === 'question' ? 'multi' : 'continue',
    title: c.mode === 'question' ? c.questionText : (c.ctaText?.trim() || 'Continue'),
    position: c.position === 'right-rail' ? 'right' : c.position,
    weight_level: c.weightLevel || 'standard',
    guide_input: c.mode === 'question' ? (c.guideInput || null) : 'none',
    page: pageNumber,
    page_link: c.mode === 'continue' && continueEffectiveType !== 'close'
        ? (c.continueAction === '' || c.continueAction == null ? null : Number(c.continueAction))
        : null,
    action_type: c.mode === 'continue' ? continueEffectiveType : null,
    action_url: c.mode === 'continue' && continueEffectiveType === 'link' ? continueLink.url : null,
    action_mail: c.mode === 'continue' && continueEffectiveType === 'link' ? continueLink.mail : null,
    action_phone: c.mode === 'continue' && continueEffectiveType === 'link' ? continueLink.phone : null,
    delay_seconds: c.delaySeconds === '' || c.delaySeconds == null ? 5 : Number(c.delaySeconds),
    enabled: c.enabled,
    bg_color: c.bgColor || null,
    text_color: c.textColor || null,
    border_color: c.borderColor || null,
    question_font_family: c.questionFontFamily || null,
    question_font_size: c.questionFontSize || null,
    question_font_weight: c.questionFontWeight || null,
    option_font_family: c.optionFontFamily || null,
    option_font_size: c.optionFontSize || null,
    option_font_weight: c.optionFontWeight || null,
    options: c.mode === 'question' ? (c.options || []).map((option, idx) => {
        const valence = option.valence || 'neutral';
        const actionType = resolveEffectiveActionType(option.actionType, option.linkValue);
        const optionLink = parseActionLink(option.linkValue);
        return {
            id: Number.isInteger(option.id) ? option.id : null,
            order: idx + 1,
            label: option.label,
            intent_tag: option.tag,
            valence,
            strength: valence === 'neutral' ? null : (option.strength || 1),
            is_hard_dq: !!option.isHardDq,
            advanced_weight: !!option.advancedWeight,
            free_text: !!option.freeText,
            page_link: actionType !== 'close'
                ? (option.action === '' || option.action == null ? null : Number(option.action))
                : null,
            action_type: actionType,
            action_url: actionType === 'link' ? optionLink.url : null,
            action_mail: actionType === 'link' ? optionLink.mail : null,
            action_phone: actionType === 'link' ? optionLink.phone : null,
            bg_color: option.bgColor || null,
            border_color: option.borderColor || null,
            hover_color: option.hoverColor || null,
            text_color: option.textColor || null,
        };
    }) : [],
    };
};

export const NavigatorContext = createContext({
    isNavigatorLayerActive: false,
    setIsNavigatorLayerActive: () => {},
    isWeightingViewActive: false,
    setIsWeightingViewActive: () => {},
    navigatorPages: {},
    setNavigatorPageConfig: () => {},
    refreshNavigatorFromServer: () => {},
    navigatorSaveState: { status: 'idle', action: null, error: null },
    setNavigatorSaveState: () => {},
    approveNavigatorPage: () => Promise.resolve(),
    approveNavigatorPages: () => Promise.resolve([]),
    saveNavigatorCheckpoint: () => Promise.resolve(false),
    toggleNavigatorEnabled: () => Promise.resolve(),
    // 'square'|'landscape'|'portrait', set once per KDF (see PdfViewer's pdfShapeRef).
    pdfShape: null,
    setPdfShape: () => {},
    // See utils/useViewportDeviceHook.
    viewportDevice: { device: "desktop", deviceOrientation: "portrait" },
    brandColors: [],
    addBrandColor: () => Promise.resolve({ ok: false }),
    removeBrandColor: () => Promise.resolve({ ok: false }),
    syncBrandColors: () => {},
    reorderBrandColors: () => {},
    docColors: {},
    addDocColor: () => Promise.resolve({ ok: false }),
    removeDocColor: () => Promise.resolve({ ok: false }),
    syncDocColors: () => {},
    reorderDocColors: () => {},
});
